import React, { FC, SyntheticEvent } from 'react';
import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';
import { useHistory } from 'react-router-dom';
import Flex from '@fenderdigital/ui/Flex';
import { Lock } from '@fenderdigital/ui/Icons';
import Heading from '@fenderdigital/ui/Heading';
import BodyText from '@fenderdigital/ui/BodyText';
import ScreenReaderOnly from '@fenderdigital/ui/ScreenReaderOnly';
import { Focus } from '@fenderdigital/ui/utils/css';
import Image, { getMapToRatios, getVariantUrls } from '@fenderdigital/react-ui-kit/image';
import { useCountriesContext } from '../../contexts/CountriesContext';
import LevelIndicator from '../LevelIndicator';
import Link from '../Link';

const MediaContainer = styled.article<{ direction: string }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  min-height: 5rem;
  flex-direction: ${({ direction }): string => direction};
`;

export const formatMinutes = (duration: number): string => {
  const minutes = Math.floor(duration / 60);
  let seconds = `${duration - minutes * 60}`;
  if (seconds.toString().length === 1) {
    seconds = `0${seconds}`;
  }
  return `${minutes}:${seconds}`;
};

const renderSongLayout = (chords: string[], artists: any[], level: string): JSX.Element => {
  return (
    <span>
      <div className="f6 silver source-sans pb1">
        {artists.map(artist => (
          <div key={uuid()}>{artist.name}</div>
        ))}
      </div>
      <div className="f5">
        <LevelIndicator currentLevel={parseInt(level, 10)} />
      </div>
      {chords.length > 0 ? (
        <div className="flex flex-row">
          <div className="f6 silver pr1">Chords</div>
          <div className="f6 flex flex-row">
            {chords.map(chord => (
              <div key={uuid()} className="mr1">
                {chord[0]}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </span>
  );
};

const renderRiffLayout = (artists: any[]): JSX.Element => {
  return (
    <div>
      <div className="pb1">
        {artists.map(artist => (
          <BodyText textColor="silver" textSize="f3" key={uuid()}>
            {artist.name}
          </BodyText>
        ))}
      </div>
    </div>
  );
};

const renderLessonLayout = (lessonType: string, lessonDuration: number): JSX.Element => {
  return (
    <>
      <BodyText textColor="gray" textSize="f3">
        {lessonType}
      </BodyText>
      {lessonDuration !== 0 ? (
        <BodyText textColor="gray" textSize="f3">
          {formatMinutes(lessonDuration)}{' '}
        </BodyText>
      ) : null}
    </>
  );
};

interface MediaProps {
  artists?: any[];
  aspectRatio?: string;
  chords?: string[];
  direction?: string;
  image: string;
  isLocked?: boolean;
  layout?: string;
  lessonDuration?: number;
  lessonType?: string;
  level?: string;
  title: string;
  type?: string;
  url?: string;
  variantType?: string;
}

const Media: FC<MediaProps> = ({
  artists = [],
  aspectRatio = '1x1',
  chords = [],
  direction = 'row',
  image,
  isLocked,
  layout = 'course',
  lessonDuration = 0,
  lessonType,
  level = '0',
  title,
  type = 'course',
  url,
  variantType = null,
}) => {
  const { isNonApprovedCountry, showNonUpgradableModal } = useCountriesContext();
  const history = useHistory();
  const width = `${direction === 'row' ? '5rem' : '10rem'}`;
  const height = `${direction === 'row' ? '5rem' : '10rem'}`;
  const images = getVariantUrls(image, aspectRatio, variantType);
  const ratio = getMapToRatios(aspectRatio);

  const handleOnClick = (): void | boolean => {
    if (isLocked) {
      if (isNonApprovedCountry) {
        showNonUpgradableModal();
        return false;
      }
      return history.push(`/plans/upgrade`);
    }
    if (!url) return false;
    return history.push(url);
  };

  return (
    <MediaContainer
      className={`media ${isLocked ? 'is-locked' : ''}`}
      direction={direction}
      onClick={handleOnClick}
    >
      <div className={`${direction === 'row' ? 'mr3' : 'mb2'}`} style={{ lineHeight: 0 }}>
        <div style={{ width, height }}>
          <Image images={images} ratio={ratio} />
        </div>
      </div>
      <div className="flex-auto mt1">
        <Flex>
          <Heading
            as="h3"
            css={css`
              line-height: 1.1;
              margin-bottom: ${({ theme: { spacing } }): string => spacing.sm2};

              a {
                ${Focus};
                color: currentColor;
                font-family: ${({ theme: { fonts } }): string => fonts.sourceSansBold};
                font-size: ${({ theme: { fontSizes } }): string => fontSizes.f3};
              }
            `}
          >
            {isLocked && (
              <Lock
                aria-hidden="true"
                data-id="locked-content"
                fontSize="1rem"
                css={`
                  margin-left: -4px;
                  margin-top: 1px;
                `}
              />
            )}
            {url ? (
              <Link
                to={isLocked ? '/plans/upgrade' : url}
                onClick={(e: SyntheticEvent): void => {
                  if (isLocked && isNonApprovedCountry) {
                    e.preventDefault();
                    showNonUpgradableModal();
                  }
                  e.stopPropagation();
                }}
              >
                {title}
                {isLocked && (
                  <ScreenReaderOnly>. Locked. Upgrade to unlock content.</ScreenReaderOnly>
                )}
              </Link>
            ) : (
              title
            )}
          </Heading>
        </Flex>
        {type === 'song' || type === 'riff' // eslint-disable-line no-nested-ternary
          ? layout === 'songs'
            ? renderSongLayout(chords, artists, level)
            : renderRiffLayout(artists)
          : renderLessonLayout(lessonType as string, lessonDuration)}
      </div>
    </MediaContainer>
  );
};

export default Media;
